import type { OnDestroy, OnInit } from '@angular/core';
import { Component } from '@angular/core';
import { FirebaseService } from 'app/services/firebase.service';
import { RoutingService } from 'app/services/routing.service';
import { ActivatedRoute } from '@angular/router';
import type { TAuthQueryParams, TAuthType } from 'app/app-routing.interfaces';
import {
  ROUTE_PATH_PARAM_AUTH_TYPE,
  ROUTE_PATH_PARAM_AUTH_TYPE_RESET,
  ROUTE_PATH_PARAM_AUTH_TYPE_SIGN_IN,
  ROUTE_PATH_PARAM_AUTH_TYPE_SIGN_UP,
  ROUTE_PATH_PARAM_AUTH_TYPE_SIGN_UP_FINAL,
} from 'app/app-routing.constants';
import { Logger } from 'app/services/logger.service';
import type {
  IResetData,
  ISignInData,
  ISignUpData,
} from './auth-page.interfaces';
import Subscriber from 'app/subscriber';
import { getCurrentAnonymousUser } from './auth-page.functions';
import { BASE_HREF } from 'app/constants';
import firebase from 'firebase/app';

const log = new Logger('AuthPageComponent');

@Component({
  templateUrl: './auth-page.component.html',
  styleUrls: ['./auth-page.component.scss'],
})
export class AuthPageComponent implements OnInit, OnDestroy {
  public type: TAuthType;

  public queryParams: TAuthQueryParams;

  public readonly ROUTE_PATH_PARAM_AUTH_TYPE_SIGN_IN =
    ROUTE_PATH_PARAM_AUTH_TYPE_SIGN_IN;

  public readonly ROUTE_PATH_PARAM_AUTH_TYPE_SIGN_UP =
    ROUTE_PATH_PARAM_AUTH_TYPE_SIGN_UP;

  public readonly ROUTE_PATH_PARAM_AUTH_TYPE_SIGN_UP_FINAL =
    ROUTE_PATH_PARAM_AUTH_TYPE_SIGN_UP_FINAL;

  public readonly ROUTE_PATH_PARAM_AUTH_TYPE_RESET =
    ROUTE_PATH_PARAM_AUTH_TYPE_RESET;

  private readonly _sub = new Subscriber();

  constructor(
    private _firebaseService: FirebaseService,
    private _routingService: RoutingService,
    private _activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this._sub.push(
      this._activatedRoute.paramMap.subscribe((paramMap) => {
        // param check implemented in AuthPageGuard
        this.type = paramMap.get(ROUTE_PATH_PARAM_AUTH_TYPE) as TAuthType;
      }),

      this._activatedRoute.queryParams.subscribe((queryParams) => {
        // queryParams check implemented in AuthPageGuard
        this.queryParams = queryParams as TAuthQueryParams;
      }),
    );
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  public async onSignInButtonClick(data?: ISignInData) {
    log.info('onSignInButtonClick', data);

    if (this.type !== ROUTE_PATH_PARAM_AUTH_TYPE_SIGN_IN) {
      this._routingService.goToAuth(ROUTE_PATH_PARAM_AUTH_TYPE_SIGN_IN);
    } else {
      const { auth: Auth } = this._firebaseService;
      const { email, password } = data;

      const anonymousUser = getCurrentAnonymousUser(Auth);

      if (!anonymousUser) return;

      try {
        await Auth.signInWithEmailAndPassword(email, password);

        await this._routingService.goToMyPage();
      } catch (error) {
        log.error('onSignInButtonClick', error);
        alert(error.message);
      }
    }
  }

  public async onSignUpButtonClick(data?: ISignUpData) {
    log.info('onSignUpButtonClick', data);

    if (this.type !== ROUTE_PATH_PARAM_AUTH_TYPE_SIGN_UP) {
      this._routingService.goToAuth(ROUTE_PATH_PARAM_AUTH_TYPE_SIGN_UP);
    } else {
      const { auth: Auth } = this._firebaseService;
      const { email, password } = data;

      const anonymousUser = getCurrentAnonymousUser(Auth);

      if (!anonymousUser) return;

      try {
        const authCredential = firebase.auth.EmailAuthProvider.credential(
          email,
          password,
        );

        await anonymousUser.linkWithCredential(authCredential);

        // await auth.currentUser.updateProfile({
        //   displayName: `${firstName} ${lastName}`
        // });

        await Auth.currentUser.sendEmailVerification({
          url: `${BASE_HREF}my-page`,
        });

        // this._routingService.goToMyPage();
        await this._routingService.goToAuth(
          ROUTE_PATH_PARAM_AUTH_TYPE_SIGN_UP_FINAL,
          {
            email: data.email,
          },
        );
      } catch (err) {
        console.error(err);
        alert(err?.message);
      }
    }
  }

  public onSignUpFinalButtonClick() {
    log.info('onSignUpFinalButtonClick');
  }

  public onResetButtonClick(data?: IResetData) {
    log.info('onResetButtonClick', data);

    if (this.type !== ROUTE_PATH_PARAM_AUTH_TYPE_RESET) {
      this._routingService.goToAuth(ROUTE_PATH_PARAM_AUTH_TYPE_RESET);
    }
  }
}
